import { Button, Field } from "@norican/react";
import { useState } from "react";

import { HelpTerm } from "@/components/help-term";
import { Notice, Page } from "@/components/page";
import { PersonPicker } from "@/components/person-picker";
import { subText, table, td, th } from "@/components/table";
import {
  type AccessGroup,
  type AccessMember,
  type AccessRequest,
  useAccessGroups,
  useMe,
  useRequestAccessAdd,
  useRequestAccessRemove,
  useRetryAccessRequest,
} from "@/lib/api";
import { mayRaise, READ_ONLY_NOTE } from "@/lib/capability";
import { errorMessage, inZoneSentence } from "@/lib/format";

/**
 * Access lists — the groups HR maintains.
 *
 * One card per group the server is configured for, and the server decides
 * which those are: this page has no group selector, no free field for a group
 * name, and nothing here can reach any other group. HR adds and removes
 * people; the change is applied within a few minutes and shown as pending
 * until it has been.
 *
 * The member table is the live directory, not a copy: it includes everyone
 * who is in the group, including people IT put there before this page
 * existed. The "via HROps" mark says which ones HR added here.
 */

// Mirrors the CHECK constraint on managed_group_request.note.
const NOTE_MAX = 500;

export function AccessPage() {
  const me = useMe().data;
  const canRaise = mayRaise(me);
  const { data, isLoading, error } = useAccessGroups(me?.access_lists_enabled === true);
  const groups = data?.groups ?? [];

  return (
    <Page
      title="Access lists"
      intro={
        <>
          An <HelpTerm id="access-list">access list</HelpTerm> is a group HR keeps up to date.
          Adding somebody gives them what the group grants; removing them takes it away again.
          Nothing here restricts anyone. Changes are applied within a few minutes and show as
          pending until they are.
        </>
      }
    >
      {!canRaise ? (
        <p className={`${subText} border-border mt-6 border-t pt-4`}>{READ_ONLY_NOTE}</p>
      ) : null}

      {me && !me.access_lists_enabled ? (
        <Notice>No access list is set up for HROps at the moment.</Notice>
      ) : error ? (
        <Notice tone="danger">{errorMessage(error, "Could not load the access lists.")}</Notice>
      ) : isLoading ? (
        <Notice>Loading the access lists…</Notice>
      ) : groups.length === 0 ? (
        <Notice>No access list is set up for HROps at the moment.</Notice>
      ) : (
        groups.map((group) => <GroupCard key={group.id} group={group} canRaise={canRaise} />)
      )}
    </Page>
  );
}

function GroupCard({ group, canRaise }: { group: AccessGroup; canRaise: boolean }) {
  const requests = group.requests ?? [];
  const members = group.members ?? [];
  const open = requests.filter((r) => r.status === "pending" || r.status === "failed");
  const done = requests.filter((r) => r.status === "applied" || r.status === "cancelled");
  const pendingFor = new Set(open.map((r) => r.subject_upn.toLowerCase()));

  return (
    <section className="mt-8">
      <h2 className="text-fg text-sm font-semibold">
        {group.display_name ?? group.id}
        <span className="text-fg-muted ml-2 font-normal">
          {members.length} {members.length === 1 ? "member" : "members"}
        </span>
      </h2>
      {group.description ? (
        <p className={`${subText} mt-1 max-w-prose`}>{group.description}</p>
      ) : null}

      {canRaise ? <AddForm groupId={group.id} /> : null}

      {open.length > 0 ? (
        <RequestTable title="Waiting" requests={open} canRaise={canRaise} />
      ) : null}

      <MemberTable
        groupId={group.id}
        members={members}
        canRaise={canRaise}
        pendingFor={pendingFor}
      />

      {done.length > 0 ? (
        <RequestTable title="Recent changes" requests={done} canRaise={false} />
      ) : null}
    </section>
  );
}

function AddForm({ groupId }: { groupId: string }) {
  const add = useRequestAccessAdd();
  const [subjectUpn, setSubjectUpn] = useState("");
  const [note, setNote] = useState("");

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!subjectUpn.trim()) {
      return;
    }
    add.mutate(
      { groupId, body: { subject_upn: subjectUpn.trim(), note: note.trim() || undefined } },
      {
        onSuccess: () => {
          setSubjectUpn("");
          setNote("");
        },
      },
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="border-border mt-4 flex flex-wrap items-end gap-4 border-t pt-4"
    >
      <PersonPicker
        label="Add a person"
        required
        size="sm"
        className="min-w-[18rem] flex-1"
        placeholder="Surname, or firstname.lastname@noricangroup.com"
        value={subjectUpn}
        onChange={setSubjectUpn}
      />
      <Field label="Note" hint="Optional. Why they join the list." className="min-w-[16rem] flex-1">
        <input
          type="text"
          maxLength={NOTE_MAX}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          className="border-border bg-bg text-fg placeholder:text-fg-muted/60 w-full rounded border px-2 py-1.5 text-sm"
        />
      </Field>
      <Button type="submit" size="sm" disabled={add.isPending || !subjectUpn.trim()}>
        Add
      </Button>
      {add.isError ? (
        <p className="text-danger-fg w-full text-sm">
          {errorMessage(add.error, "The person could not be added.")}
        </p>
      ) : add.isSuccess ? (
        <p className={`${subText} w-full`}>
          Requested. It is applied within a few minutes and shows as pending until then.
        </p>
      ) : null}
    </form>
  );
}

function MemberTable({
  groupId,
  members: all,
  canRaise,
  pendingFor,
}: {
  groupId: string;
  members: AccessMember[];
  canRaise: boolean;
  pendingFor: Set<string>;
}) {
  const [filter, setFilter] = useState("");
  const needle = filter.trim().toLowerCase();
  const members = needle
    ? all.filter((m) =>
        [m.display_name, m.upn, m.job_title, m.department]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(needle)),
      )
    : all;

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-fg text-sm font-semibold">Members</h3>
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter by name, sign-in, title or department"
          aria-label="Filter members"
          className="border-border bg-bg text-fg placeholder:text-fg-muted/60 min-w-[18rem] rounded border px-2 py-1 text-sm"
        />
      </div>
      {members.length === 0 ? (
        <Notice>{needle ? "Nobody matches that filter." : "The group has no members."}</Notice>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className={table}>
            <thead>
              <tr>
                <th scope="col" className={th}>
                  Person
                </th>
                <th scope="col" className={th}>
                  Title
                </th>
                <th scope="col" className={th}>
                  Department
                </th>
                <th scope="col" className={th}>
                  Added
                </th>
                <th scope="col" className={th}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <MemberRow
                  key={member.id}
                  groupId={groupId}
                  member={member}
                  canRaise={canRaise}
                  pending={pendingFor.has((member.upn ?? "").toLowerCase())}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MemberRow({
  groupId,
  member,
  canRaise,
  pending,
}: {
  groupId: string;
  member: AccessMember;
  canRaise: boolean;
  pending: boolean;
}) {
  const remove = useRequestAccessRemove();
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState("");

  const submit = () => {
    if (!member.upn || !note.trim()) {
      return;
    }
    remove.mutate(
      { groupId, body: { subject_upn: member.upn, note: note.trim() } },
      { onSuccess: () => setAsking(false) },
    );
  };

  return (
    <tr>
      <td className={`${td} text-fg`}>
        {member.display_name ?? member.upn}
        <div className={subText}>
          {member.upn}
          {member.account_enabled === false ? " · disabled" : ""}
          {member.user_type === "Guest" ? " · guest" : ""}
        </div>
      </td>
      <td className={`${td} text-fg`}>{member.job_title ?? ""}</td>
      <td className={`${td} text-fg`}>{member.department ?? ""}</td>
      <td className={`${td} ${subText}`}>{member.added_via_hrops ? "via HROps" : ""}</td>
      <td className={`${td} text-right whitespace-nowrap`}>
        {!canRaise ? null : pending ? (
          <span className={subText}>change pending</span>
        ) : asking ? (
          <span className="flex items-center justify-end gap-2">
            <input
              type="text"
              autoFocus
              maxLength={NOTE_MAX}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Why (required)"
              aria-label="Reason for removal"
              className="border-border bg-bg text-fg placeholder:text-fg-muted/60 w-56 rounded border px-2 py-1 text-sm"
            />
            <Button size="sm" disabled={remove.isPending || !note.trim()} onClick={submit}>
              Remove
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAsking(false)}>
              Cancel
            </Button>
          </span>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setAsking(true)}>
            Remove
          </Button>
        )}
        {remove.isError ? (
          <p className="text-danger-fg mt-1 text-xs whitespace-normal">
            {errorMessage(remove.error, "The removal could not be requested.")}
          </p>
        ) : null}
      </td>
    </tr>
  );
}

function RequestTable({
  title,
  requests,
  canRaise,
}: {
  title: string;
  requests: AccessRequest[];
  canRaise: boolean;
}) {
  const retry = useRetryAccessRequest();
  return (
    <div className="mt-6">
      <h3 className="text-fg text-sm font-semibold">
        {title}
        <span className="text-fg-muted ml-2 font-normal">{requests.length}</span>
      </h3>
      <div className="mt-3 overflow-x-auto">
        <table className={table}>
          <thead>
            <tr>
              <th scope="col" className={th}>
                Change
              </th>
              <th scope="col" className={th}>
                Person
              </th>
              <th scope="col" className={th}>
                Requested
              </th>
              <th scope="col" className={th}>
                State
              </th>
              <th scope="col" className={th}>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr key={request.id}>
                <td className={`${td} text-fg`}>
                  {request.operation === "add" ? "Add" : "Remove"}
                  {request.note ? (
                    <span className="text-fg-muted mt-0.5 block max-w-[28rem] text-xs whitespace-normal">
                      {request.note}
                    </span>
                  ) : null}
                </td>
                <td className={`${td} text-fg`}>{request.subject_upn}</td>
                <td className={`${td} text-fg whitespace-nowrap`}>
                  {inZoneSentence(request.requested_at, null)}
                  <div className={subText}>{request.requester_upn}</div>
                </td>
                <td className={`${td} text-fg`}>
                  {request.status === "pending"
                    ? "pending"
                    : request.status === "applied"
                      ? `applied${request.applied_at ? " " + inZoneSentence(request.applied_at, null) : ""}`
                      : request.status}
                  {request.error ? (
                    <span className="text-danger-fg mt-0.5 block max-w-[28rem] text-xs whitespace-normal">
                      {request.error}
                    </span>
                  ) : null}
                </td>
                <td className={`${td} text-right`}>
                  {canRaise && request.status === "failed" ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={retry.isPending}
                      onClick={() => retry.mutate(request.id)}
                    >
                      Retry
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {retry.isError ? (
        <Notice tone="danger">{errorMessage(retry.error, "The retry was refused.")}</Notice>
      ) : null}
    </div>
  );
}
